import {
  BoneGraftIcon, SpineStackIcon, ToothIcon, EarIcon, NonUnionIcon,
  ProcedureCoveredIcon, GraftUtilizedIcon, CaseRateBenefitIcon,
} from '../components/FlexiOssCinematic/PhilHealthIcons';

/* ============================================================================
   Single source of truth for the PhilHealth RVS / subsidy reference.
   Patient-literacy oriented: exposes ONLY the "Base PhilHealth Subsidy
   Allowance" per procedure. Professional Fee (PF) and Health Facility Fee
   (HFF) are intentionally NEVER itemized anywhere on the site.
   ========================================================================== */

export const ORTHO = { key: 'Ortho', label: 'FlexiOss® Ortho', href: '/ortho', color: '#3b82f6' };
export const DENT = { key: 'Dent', label: 'FlexiOss® Dent', href: '/dent', color: '#E91E8C' };

export const CATEGORIES = [
  {
    key: 'ortho-general', icon: BoneGraftIcon,
    label: 'General Orthopedic Structural Reconstruction',
    sub: 'General Orthopedics',
    cases: [
      { code: '20900', name: 'Minor Bone Reconstruction Baseline', variants: [ORTHO],
        size: '1 cm – 2 cm structural blocks',
        app: 'Standard small-defect filling, burr-hole packing, and minor structural stabilization.',
        subsidy: '₱20,553.00' },
      { code: '20902', name: 'Major Bone Reconstruction Baseline', variants: [ORTHO],
        size: '5 cm × 5 cm or larger customized blocks',
        app: 'Large-segment bone-defect repairs, tumor cavitary packing, and complex skeletal stabilization.',
        subsidy: '₱35,100.00' },
    ],
  },
  {
    key: 'spine', icon: SpineStackIcon,
    label: 'Spinal Arthrodesis & Matrix Advancements',
    sub: 'Spine Reconstruction',
    cases: [
      { code: '20930', name: 'Morselized Spinal Matrix Application', variants: [ORTHO],
        size: '2 cm – 3 cm tailored strips per level',
        app: 'Composite padding shaped for stabilization within interbody fusion cages.',
        subsidy: '₱23,634.00' },
      { code: '20931', name: 'Structural Spinal Columnar Support', variants: [ORTHO],
        size: 'Structural strips / blocks cut to fit cage geometries',
        app: 'Pre-formed structural biomaterial designed for multi-level anterior column fusions.',
        subsidy: '₱35,919.00' },
      { code: '22612', name: 'Arthrodesis, Posterior Trunk (With Graft)', variants: [ORTHO],
        size: '5 cm × 5 cm or multi-strip structural combinations',
        app: 'Posterior trunk fusion with adjunctive bone-graft packing across the instrumented segment.',
        subsidy: '₱101,400.00' },
    ],
  },
  {
    key: 'dental', icon: ToothIcon,
    label: 'Dental, Dentoalveolar & Oral-Maxillofacial Reconstruction',
    sub: 'Maxillofacial',
    cases: [
      { code: '21210', name: 'Maxillary & Sinus Floor Reconstructive Grafting', variants: [DENT],
        size: '1 cm × 1 cm or 1 cm × 2 cm customized dimensions',
        app: 'Sinus floor elevations, maxillary ridge augmentation, and post-extraction socket preservation.',
        subsidy: '₱90,675.00' },
      { code: '21215', name: 'Mandibular Ridge & Body Reconstructive Grafting', variants: [DENT],
        size: '2 cm × 3 cm or customized structural strip adaptations',
        app: 'Horizontal or vertical lower-jaw ridge augmentation to secure a foundation prior to implant loading.',
        subsidy: '₱107,250.00' },
      { code: '21044', name: 'Excision of Mandibular / Maxillary Cyst or Tumor', variants: [DENT, ORTHO],
        variantNote: 'either variant, dependent on defect cavity dimensions',
        size: '2 cm to 5 cm structural block packing',
        app: 'Cavity obliteration and structural packing following enucleation of a jaw cyst or benign tumor.',
        subsidy: '₱122,850.00' },
      { code: '42210', name: 'Alveolar Cleft Palate Repair (With Bone Grafting)', variants: [DENT],
        size: '1 cm × 2 cm tailored sections',
        app: 'Bridging the alveolar cleft with a moldable graft to restore the maxillary arch continuity.',
        subsidy: '₱44,226.00' },
    ],
  },
  {
    key: 'bundled', icon: NonUnionIcon,
    label: 'Bundled Reconstructive & Non-Union Indications',
    sub: 'Non-Union Repair',
    cases: [
      { code: '24435', name: 'Humerus Non-Union / Malunion Repair (With Graft)', variants: [ORTHO],
        size: '5 cm × 5 cm or 5 cm × 10 cm structural sheets',
        app: 'Revision of humeral non-union or malunion with structural graft augmentation at the fracture interface.',
        subsidy: '₱64,350.00' },
      { code: '25440', name: 'Radius or Ulna Non-Union Repair (With Graft)', variants: [ORTHO],
        size: '2 cm – 5 cm longitudinal strip adaptations',
        app: 'Forearm non-union reconstruction with graft strips conformed to the diaphyseal defect.',
        subsidy: '₱58,500.00' },
      { code: '27722', name: 'Tibia Non-Union / Malunion Repair (With Graft)', variants: [ORTHO],
        size: '5 cm × 10 cm large structural segments',
        app: 'Tibial non-union repair using large structural segments to restore load-bearing continuity.',
        subsidy: '₱76,050.00' },
    ],
  },
  {
    key: 'otology', icon: EarIcon,
    label: 'Otology & Neurotology (Skull Base Reconstruction)',
    sub: 'Skull Base',
    cases: [
      { code: '69635', name: 'Tympanoplasty with Mastoidectomy Cavity Obliteration', variants: [DENT, ORTHO],
        variantNote: 'either variant, dependent on mastoidectomy bowl dimensions',
        size: '1 cm – 2 cm tailored structural fragments',
        app: 'Mastoid bowl obliteration to eliminate chronic post-operative drainage and tissue retraction.',
        subsidy: '₱46,500.00' },
      { code: '61520', name: 'Craniotomy for Excision of Brain Tumor, Cerebellopontine Angle (CPA)', variants: [ORTHO],
        size: 'Structural strip or 2 cm – 5 cm customized adaptations',
        app: 'Posterior fossa cranial defect closure to seal the skull base against cerebrospinal-fluid (CSF) leaks.',
        subsidy: '₱184,500.00' },
    ],
  },
];

export const SIZING_DISCLAIMER =
  'The product variant selections and dimensional sizing recommendations listed within this directory serve as reference guidelines for standard anatomical reconstructions. The final dimensions, length, and specific variant of the FlexiOss® biomaterial deployed must be determined solely by the attending operating physician or surgeon based on real-time intraoperative defect boundaries, clinical judgment, and individual patient presentation.';

export const HOW_STEPS = [
  { icon: ProcedureCoveredIcon, title: 'The procedure is covered', text: 'PhilHealth provides a fixed subsidy allowance for the surgical procedure itself — never for any specific commercial brand of biomaterial.' },
  { icon: GraftUtilizedIcon, title: 'FlexiOss® is the premium upgrade', text: 'FlexiOss® is the advanced 3rd-generation synthetic graft the surgeon uses to fill or obliterate the defect during that covered procedure.' },
  { icon: CaseRateBenefitIcon, title: 'A co-payment covers the upgrade', text: 'Your subsidy allowance is credited to the hospital bill; the premium material is typically settled through a transparent institutional or out-of-pocket co-payment.' },
];

export const SURGEON_POINTS = [
  'Map your operative report to the primary RVS code so the procedure\u2019s subsidy allowance is correctly credited.',
  'For multiple procedures in one session, sequence the highest-valued code first (100%), with each additional one at 50%.',
  'Document defect size, indication, and graft utilization clearly to support a smooth claim at adjudication.',
  'Confirm the current subsidy allowance against the latest PhilHealth circular for your facility tier.',
];

export const PATIENT_POINTS = [
  'Your PhilHealth subsidy allowance is automatically credited toward your total hospital bill.',
  'Premium biomaterials such as FlexiOss® are an advanced upgrade, usually settled through a separate co-payment.',
  'Ask whether your private HMO or insurance can offset part of the biomaterial co-payment.',
  'Confirm your final subsidy and co-payment with your hospital\u2019s PhilHealth and billing desks before your procedure.',
];

export const RVS_FAQS = [
  { q: 'Does PhilHealth pay for FlexiOss® directly?', a: 'No. PhilHealth provides a fixed subsidy allowance for the surgical procedure — not for any specific commercial biomaterial brand. FlexiOss® is the advanced synthetic graft the surgeon uses within that covered procedure, and the subsidy allowance is credited to the hospital bill.' },
  { q: 'What is the “Base PhilHealth Subsidy Allowance”?', a: 'It is the baseline credit PhilHealth applies to your total hospital bill for a covered procedure. Because FlexiOss® is a premium, 3rd-generation biomaterial, patients typically navigate a separate institutional or out-of-pocket co-payment for the material itself.' },
  { q: 'Why is there a co-payment for FlexiOss®?', a: 'Standard subsidy allowances are calibrated around legacy materials. FlexiOss® is an advanced bio-engineered matrix clinically shown to accelerate healing kinetics and reduce the risk of non-unions and revision surgery, so the premium upgrade is settled through a transparent co-payment structure.' },
  { q: 'How does the Co-Payment Subsidy Estimator work for combined procedures?', a: 'When two or more covered procedures are performed in one session, PhilHealth credits 100% of the highest-valued subsidy allowance and 50% of each additional one. The estimator applies this automatically to show your combined baseline subsidy.' },
  { q: 'Which FlexiOss® variant corresponds to each RVS code?', a: 'Orthopedic, spinal, non-union, and most skull-base procedures use FlexiOss® Ortho; dental, dentoalveolar, and oral-maxillofacial procedures use FlexiOss® Dent. For jaw-cyst excision (RVS 21044) and mastoid obliteration (RVS 69635) either variant may apply depending on the dimensional boundaries confirmed intraoperatively by the surgeon.' },
  { q: 'Are veterinary procedures eligible for PhilHealth?', a: 'No. Veterinary procedures and the FlexiOss® Vet product line are entirely ineligible for human PhilHealth subsidy allowances and are intentionally excluded from this directory.' },
  { q: 'How do I confirm the exact, current subsidy amount?', a: 'Subsidy allowances can change via new PhilHealth circulars. Verify current values through the official PhilHealth portal and confirm your final coverage and co-payment with your hospital\u2019s PhilHealth and billing desks.' },
];

export const parsePeso = (s) => { const n = parseFloat(String(s).replace(/[₱,\s]/g, '')); return isNaN(n) ? 0 : n; };
export const fmtPeso = (n) => '₱' + n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
export const caseByKey = (key) => {
  const [k, idx] = key.split('::');
  const cat = CATEGORIES.find((c) => c.key === k);
  return cat ? { cat, c: cat.cases[parseInt(idx, 10)] } : null;
};
